import { useLocation, REGIONS } from "@/lib/locations";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { MapPin, ShieldCheck, ArrowRight, CheckCircle2, Star } from "lucide-react";
import { ALL_NEIGHBORHOOD_SLUGS, getNeighborhoodData } from "@/lib/neighborhoods-data";

// Helper to get neighborhoods for a city
const getNeighborhoods = (citySlug: string): { name: string, slug: string }[] => {
  const matchingNeighborhoods = ALL_NEIGHBORHOOD_SLUGS
    .map(slug => getNeighborhoodData(slug))
    .filter(data => data && data.citySlug === citySlug)
    .map(data => ({ name: data!.name, slug: data!.id }));
    
  if (matchingNeighborhoods.length > 0) {
    return matchingNeighborhoods;
  }

  return [
    { name: "Surrounding Residential Areas", slug: "" },
    { name: "Local Subdivisions", slug: "" },
    { name: "Nearby Communities", slug: "" },
    { name: "City Center", slug: "" },
    { name: "Neighboring Districts", slug: "" },
    { name: "Suburban Areas", slug: "" },
    { name: "Local School Districts", slug: "" }
  ];
};

const getLifestyleSentence = (cityName: string) => {
  const variations = [
    `Many ${cityName} homeowners have busy schedules, family routines, pets, and seasonal dust buildup, making consistent cleaning a practical way to keep the home easier to maintain.`,
    `Homes in ${cityName} deal with dust buildup, daily wear, pet hair, and busy family schedules. Our cleaning services help keep your home more comfortable and hygienic.`,
    `Whether you're commuting, working from home, or managing a busy family life in ${cityName}, keeping up with household chores can be overwhelming. We make it easy to maintain a spotless space.`,
    `Living in ${cityName} means you have better things to do than scrub floors. Our professional teams tackle the dust, pet hair, and daily messes so you don't have to.`
  ];
  
  const index = cityName.length % variations.length;
  return variations[index];
};

export const LocalSEOContent = () => {
  const location = useLocation();
  const currentRegion = REGIONS[location.regionSlug];

  if (!currentRegion) return null;

  if (location.type === "region") {
    return (
      <section className="py-20 bg-background border-t">
        <div className="container max-w-5xl mx-auto">


          <div className="bg-muted/30 rounded-3xl p-8 md:p-12 border shadow-sm mb-12">
            <h3 className="text-2xl font-bold mb-6 text-center">Cities We Serve in {location.name}</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {currentRegion.cities.map((city) => (
                <Link
                  key={city}
                  to={`/locations/${city.toLowerCase().replace(/\s+/g, "-")}`}
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors p-2 rounded-lg hover:bg-primary/5"
                >
                  <MapPin className="w-4 h-4 text-primary shrink-0" />
                  <span className="font-medium">{city}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="mb-16">
            <h3 className="text-3xl font-bold mb-8 text-center">Cleaning Services Available in {location.name}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: "House Cleaning", slug: "standard-clean" },
                { name: "Deep Cleaning", slug: "deep-clean" },
                { name: "Move-In / Move-Out", slug: "move-in-out" },
                { name: "Recurring Cleaning", slug: "recurring-clean" },
                { name: "Apartment Cleaning", slug: "apartment-clean" },
                { name: "One-Time Cleaning", slug: "one-time-clean" },
                { name: "Weekly Cleaning", slug: "weekly-clean" }
              ].map((svc) => (
                <Link key={svc.slug} to={`/services/${svc.slug}`} className="bg-card p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all text-center font-bold text-lg hover:text-primary">
                  {svc.name}
                </Link>
              ))}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div className="bg-primary/5 rounded-3xl p-8 border border-primary/10">
              <h3 className="text-2xl font-bold mb-6">Why Choose Uplevel Cleaning in {location.name}</h3>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-primary shrink-0" /><span className="font-medium">200% Satisfaction Guarantee</span></li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-primary shrink-0" /><span className="font-medium">Easy online booking</span></li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-primary shrink-0" /><span className="font-medium">Transparent pricing with no hidden fees</span></li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-primary shrink-0" /><span className="font-medium">Professional, vetted cleaners</span></li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-primary shrink-0" /><span className="font-medium">Flexible one-time and recurring options</span></li>
                <li className="flex items-start gap-3"><CheckCircle2 className="w-6 h-6 text-primary shrink-0" /><span className="font-medium">Clear policies and expectations</span></li>
              </ul>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-6">How It Works</h3>
              <div className="space-y-6">
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold shrink-0">1</div>
                  <div>
                    <h4 className="font-bold text-lg">Get your price</h4>
                    <p className="text-muted-foreground">Enter your home details for an instant online estimate.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold shrink-0">2</div>
                  <div>
                    <h4 className="font-bold text-lg">Book online</h4>
                    <p className="text-muted-foreground">Choose your date and time in 60 seconds.</p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary text-white flex items-center justify-center font-bold shrink-0">3</div>
                  <div>
                    <h4 className="font-bold text-lg">Enjoy a clean home</h4>
                    <p className="text-muted-foreground">Our professionals handle the rest.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-16 text-center max-w-3xl mx-auto">
            <h3 className="text-2xl font-bold mb-4">Local Cleaning Needs in {location.name}</h3>
            <p className="text-muted-foreground text-lg">
              Many households in {location.name} juggle busy schedules, commuting, pets, and family life. Between seasonal dust, moving, or just keeping up with daily messes, managing a home can be overwhelming. Our recurring cleaning, deep cleaning, and apartment cleaning services are designed to give you your time back while keeping your living space comfortable and hygienic.
            </p>
          </div>

          <div className="mb-16 max-w-3xl mx-auto">
            <h3 className="text-3xl font-bold mb-8 text-center">Frequently Asked Questions</h3>
            <div className="space-y-4">
              <div className="bg-card border rounded-xl p-6">
                <h4 className="font-bold text-lg mb-2">What areas do you serve in {location.name}?</h4>
                <p className="text-muted-foreground">We serve the entire {location.name} region, including all major surrounding cities and neighborhoods.</p>
              </div>
              <div className="bg-card border rounded-xl p-6">
                <h4 className="font-bold text-lg mb-2">How much does house cleaning cost in {location.name}?</h4>
                <p className="text-muted-foreground">Pricing depends on your home's square footage, bedrooms, bathrooms, and the level of service. You can get an exact price instantly using our online calculator.</p>
              </div>
              <div className="bg-card border rounded-xl p-6">
                <h4 className="font-bold text-lg mb-2">Do you offer deep cleaning in {location.name}?</h4>
                <p className="text-muted-foreground">Yes! Our deep cleaning service is perfect for first-time visits, seasonal refreshes, or homes that haven't been professionally cleaned recently.</p>
              </div>
              <div className="bg-card border rounded-xl p-6">
                <h4 className="font-bold text-lg mb-2">Can I book recurring service?</h4>
                <p className="text-muted-foreground">Absolutely. We offer weekly, bi-weekly, and every 4 weeks recurring services with discounted rates.</p>
              </div>
              <div className="bg-card border rounded-xl p-6">
                <h4 className="font-bold text-lg mb-2">How do I get a quote?</h4>
                <p className="text-muted-foreground">Simply click "Book Now" or "Get Instant Price" anywhere on our site to enter your details and see your price in 60 seconds.</p>
              </div>
            </div>
          </div>

          <div className="text-center bg-primary text-primary-foreground rounded-3xl p-12">
            <h3 className="text-3xl font-bold mb-6">Book House Cleaning in {location.name} Today</h3>
            <Button size="lg" asChild className="bg-white text-primary hover:bg-gray-100 font-bold text-lg px-8 h-14 rounded-xl shadow-lg">
              <Link to="/book" aria-label="Get instant price for your region">Get Instant Price</Link>
            </Button>
          </div>
        </div>
      </section>
    );
  }

  // CITY PAGE CONTENT
  const neighborhoods = getNeighborhoods(location.slug);
  const nearbyCities = currentRegion.cities.filter(c => c !== location.name).slice(0, 8);

  return (
    <div className="bg-background">
      {/* House Cleaning in [CITY] */}
      <section className="py-20 border-t bg-muted/10">
        <div className="container max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            House Cleaning in {location.name}
          </h2>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            Uplevel Cleaning provides professional house cleaning in {location.name} for busy homeowners who want a cleaner, healthier home without spending their free time scrubbing. From routine maintenance cleaning to deep cleaning and move-in/move-out cleaning, our services are designed for local households that value reliability, clear pricing, and easy online booking.
          </p>
          <p className="text-lg text-muted-foreground leading-relaxed mb-8">
            {getLifestyleSentence(location.name)}
          </p>
          <div className="flex flex-wrap justify-center gap-4 mt-4">
            <Link to="/services" className="text-primary font-semibold hover:underline">House cleaning services in {location.name}</Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/services/deep-clean" className="text-primary font-semibold hover:underline">Deep cleaning in {location.name}</Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/services/move-in-out" className="text-primary font-semibold hover:underline">Move-out cleaning in {location.name}</Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/why-recurring" className="text-primary font-semibold hover:underline">Recurring maid service in {location.name}</Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/services" className="text-primary font-semibold hover:underline">Professional apartment cleaning in {location.name}</Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/cleaning-policies" className="text-primary font-semibold hover:underline">Cleaning Policies</Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/terms-of-service" className="text-primary font-semibold hover:underline">Terms of Service</Link>
            <span className="text-muted-foreground">•</span>
            <Link to="/privacy-policy" className="text-primary font-semibold hover:underline">Privacy Policy</Link>
          </div>
        </div>
      </section>

      {/* More Services Section */}
      <section className="py-20 border-t">
        <div className="container max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-10 text-center">More Cleaning Services in {location.name}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: "Weekly Cleaning", slug: "weekly-clean", desc: `Keep your ${location.name} home consistently spotless week after week.` },
              { name: "Bi-Weekly Cleaning", slug: "biweekly-clean", desc: `Our most popular recurring service in ${location.name}.` },
              { name: "One-Time Cleaning", slug: "one-time-clean", desc: `A quick refresh or deep clean without the commitment.` },
              { name: "Same-Day Cleaning", slug: "same-day-clean", desc: `Fast dispatch for emergency cleaning needs.` },
              { name: "Apartment Cleaning", slug: "apartment-clean", desc: `Specialized condo and apartment cleaning services.` },
              { name: "Eco-Friendly Cleaning", slug: "eco-friendly-clean", desc: `Non-toxic green cleaning safe for kids and pets.` },
              { name: "Cleaning for Pet Owners", slug: "pet-friendly-clean", desc: `Targeted pet hair and odor removal.` },
              { name: "Airbnb Turnover Cleaning", slug: "airbnb-clean", desc: `Reliable 5-star cleaning for vacation rentals.` }
            ].map((svc) => (
              <div key={svc.slug} className="bg-background p-6 rounded-2xl border shadow-sm hover:shadow-md transition-all">
                <h4 className="font-bold text-lg mb-2 text-foreground">
                  <Link to={`/services/${svc.slug}`} className="hover:text-primary transition-colors">
                    {svc.name}
                  </Link>
                </h4>
                <p className="text-muted-foreground text-sm">{svc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Areas & Trust Section */}
      <section className="py-20">
        <div className="container max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16">
            <div>
              <h2 className="text-3xl font-bold mb-6">Popular Areas We Serve in {location.name}</h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                {neighborhoods.map((hood, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-primary opacity-70" />
                    {hood.slug ? (
                      <Link to={`/neighborhood/${hood.slug}`} className="font-medium text-muted-foreground hover:text-primary transition-colors">
                        {hood.name}
                      </Link>
                    ) : (
                      <span className="font-medium text-muted-foreground">{hood.name}</span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="text-muted-foreground mb-8">
                Whether you live near {neighborhoods[0]?.name}, {neighborhoods[1]?.name}, or surrounding communities, Uplevel Cleaning makes it easy to book professional cleaning services online and get a transparent estimate.
              </p>
              
              <h3 className="text-xl font-bold mb-4">Nearby Areas We Serve</h3>
              <div className="flex flex-wrap gap-2">
                {nearbyCities.map((city) => (
                  <Link 
                    key={city} 
                    to={`/locations/${city.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-sm px-3 py-1.5 bg-muted rounded-full hover:bg-primary/10 hover:text-primary transition-colors"
                  >
                    {city}
                  </Link>
                ))}
              </div>
            </div>

            <div className="bg-primary/5 rounded-3xl p-8 border border-primary/10">
              <h2 className="text-2xl font-bold mb-6">Why {location.name} Homeowners Choose Uplevel Cleaning</h2>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                  <span className="font-medium">200% Satisfaction Guarantee</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                  <span className="font-medium">Easy online booking</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                  <span className="font-medium">Transparent pricing with no hidden fees</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                  <span className="font-medium">Professional, vetted cleaners</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                  <span className="font-medium">Flexible one-time and recurring cleaning options</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                  <span className="font-medium">Clear cleaning policies and expectations</span>
                </li>
              </ul>
              <p className="text-sm text-muted-foreground">
                Uplevel Cleaning is built for homeowners who want a reliable, professional cleaning experience without confusion, missed expectations, or surprise pricing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* House Cleaning FAQs in [City] */}
      <section className="py-20 bg-muted/10 border-t">
        <div className="container max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-10 text-center">House Cleaning FAQs in {location.name}</h2>
          <div className="space-y-4">
            <div className="bg-background border rounded-2xl p-6 shadow-sm">
              <h4 className="font-bold text-lg mb-2">How much does house cleaning cost in {location.name}?</h4>
              <p className="text-muted-foreground">Pricing is based on the size of your home (square footage, bedrooms, bathrooms) and the type of cleaning needed. You can get an exact, flat-rate price instantly using our online booking tool.</p>
            </div>
            <div className="bg-background border rounded-2xl p-6 shadow-sm">
              <h4 className="font-bold text-lg mb-2">Do you offer deep cleaning in {location.name}?</h4>
              <p className="text-muted-foreground">Yes! Our deep cleaning service tackles buildup, baseboards, and hard-to-reach areas. It's highly recommended for first-time visits.</p>
            </div>
            <div className="bg-background border rounded-2xl p-6 shadow-sm">
              <h4 className="font-bold text-lg mb-2">Do you offer move-out cleaning in {location.name}?</h4>
              <p className="text-muted-foreground">Yes, our move-out cleaning service is designed to help you prepare your home for the next resident or get your deposit back. We clean inside cabinets, appliances, and more.</p>
            </div>
            <div className="bg-background border rounded-2xl p-6 shadow-sm">
              <h4 className="font-bold text-lg mb-2">Can I book recurring cleaning in {location.name}?</h4>
              <p className="text-muted-foreground">Yes, we offer weekly, bi-weekly, and every 4 weeks recurring services. Recurring customers also receive discounted rates.</p>
            </div>
            <div className="bg-background border rounded-2xl p-6 shadow-sm">
              <h4 className="font-bold text-lg mb-2">Do I need to be home during the cleaning?</h4>
              <p className="text-muted-foreground">No, you do not need to be home. Many of our {location.name} clients provide entry instructions (like a lockbox or door code) so we can clean while they are at work or running errands.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Local CTA */}
      <section className="py-24 bg-primary text-primary-foreground relative overflow-hidden border-t">
        <div className="absolute inset-0 bg-[url('https://vibe.filesafe.space/1776798830316075366/assets/5e04562c-1143-4be5-91d2-98e30a94c625.png')] bg-cover bg-center opacity-10 mix-blend-overlay"></div>
        <div className="container relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">Get House Cleaning in {location.name} Without the Stress</h2>
          <p className="text-xl opacity-90 mb-10">
            Ready to see what cleaning would cost for your home? Use our online booking system to enter your home details, choose your service, and get a transparent estimate in minutes.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" asChild className="bg-white text-primary hover:bg-gray-100 font-bold text-lg px-8 h-14 rounded-xl shadow-xl">
              <Link to="/book" aria-label="Get instant price for city service">Get Instant Price</Link>
            </Button>
            <Button size="lg" variant="outline" asChild className="bg-transparent border-white text-white hover:bg-white/10 hover:text-white font-bold text-lg px-8 h-14 rounded-xl">
              <Link to="/cleaning-policies">View Cleaning Policies</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
